import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { BASE } from '../utils/api'

export function VerifyEmailPage() {
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [code, setCode] = useState(['', '', '', '', '', ''])
  const [loading, setLoading] = useState(false)
  const [resending, setResending] = useState(false)
  const [error, setError] = useState('')
  const [resendSent, setResendSent] = useState(false)
  const [resendCountdown, setResendCountdown] = useState(0)
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])

  useEffect(() => {
    document.documentElement.classList.remove('dark')
    document.body.style.overflow = 'auto'
    document.body.style.height = 'auto'
    const e = new URLSearchParams(window.location.search).get('email') ?? ''
    setEmail(decodeURIComponent(e))
    setTimeout(() => inputRefs.current[0]?.focus(), 100)
    return () => {
      document.body.style.overflow = ''
      document.body.style.height = ''
    }
  }, [])

  useEffect(() => {
    if (resendCountdown <= 0) return
    const t = setTimeout(() => setResendCountdown(c => c - 1), 1000)
    return () => clearTimeout(t)
  }, [resendCountdown])

  function handleDigit(index: number, value: string) {
    const digit = value.replace(/\D/g, '').slice(-1)
    const next = [...code]
    next[index] = digit
    setCode(next)
    setError('')
    if (digit && index < 5) {
      inputRefs.current[index + 1]?.focus()
    }
    if (next.every(d => d !== '') && digit) {
      submitCode(next.join(''))
    }
  }

  function handleKeyDown(index: number, e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus()
    }
    if (e.key === 'ArrowLeft' && index > 0) inputRefs.current[index - 1]?.focus()
    if (e.key === 'ArrowRight' && index < 5) inputRefs.current[index + 1]?.focus()
  }

  function handlePaste(e: React.ClipboardEvent) {
    e.preventDefault()
    const digits = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6)
    if (!digits) return
    const next = [...code]
    for (let i = 0; i < 6; i++) next[i] = digits[i] ?? ''
    setCode(next)
    setError('')
    const focusIdx = Math.min(digits.length, 5)
    inputRefs.current[focusIdx]?.focus()
    if (digits.length === 6) submitCode(digits)
  }

  async function submitCode(fullCode: string) {
    if (!email) { setError('Email не найден. Пройдите регистрацию заново.'); return }
    setLoading(true)
    setError('')
    try {
      const res = await fetch(`${BASE}/auth/verify-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code: fullCode }),
      })
      const data = await res.json() as { accessToken?: string; refreshToken?: string; message?: string; alreadyVerified?: boolean }
      if (!res.ok) throw new Error(data.message ?? 'Неверный код')
      if (data.alreadyVerified) {
        // Email already verified — redirect to login (do not issue tokens without password)
        navigate('/login?tab=login&hint=alreadyVerified')
        return
      }
      if (data.accessToken) {
        localStorage.setItem('bst-token', data.accessToken)
        localStorage.setItem('bst-refresh', data.refreshToken ?? '')
        localStorage.setItem('bst-auth', 'true')
        window.location.href = '/dashboard'
      }
    } catch (err) {
      setError((err as Error).message)
      setCode(['', '', '', '', '', ''])
      setTimeout(() => inputRefs.current[0]?.focus(), 50)
    } finally {
      setLoading(false)
    }
  }

  async function handleResend() {
    if (!email || resendCountdown > 0) return
    setResending(true)
    setError('')
    try {
      await fetch(`${BASE}/auth/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      })
      setResendSent(true)
      setResendCountdown(60)
    } catch {
      setError('Не удалось отправить код. Попробуйте позже.')
    } finally {
      setResending(false)
    }
  }

  const maskedEmail = email
    ? email.replace(/^(.{2})(.*)(@.*)$/, (_, a, b, c) => a + '*'.repeat(Math.min(b.length, 4)) + c)
    : '...'

  return (
    <div style={{
      minHeight: '100vh',
      background: '#f5f5f5',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px',
      fontFamily: "'Inter', 'Segoe UI', system-ui, sans-serif",
    }}>
      <div style={{ position: 'fixed', inset: 0, pointerEvents: 'none', overflow: 'hidden', zIndex: 0 }}>
        <div style={{
          position: 'absolute', top: '30%', left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '600px', height: '400px',
          background: 'radial-gradient(ellipse, rgba(91,106,240,0.08) 0%, transparent 70%)',
          borderRadius: '50%',
        }} />
      </div>

      <div style={{
        position: 'relative', zIndex: 1,
        width: '100%', maxWidth: '420px',
        background: '#ffffff',
        border: '1px solid #e0e0e0',
        borderRadius: '20px',
        padding: '40px',
        boxShadow: '0 8px 40px rgba(0,0,0,0.08)',
      }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: '28px' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
            <div style={{
              width: '36px', height: '36px', borderRadius: '10px',
              background: '#5b6af0',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontWeight: 900, color: '#fff', fontSize: '16px',
              boxShadow: '0 4px 16px rgba(91,106,240,0.25)',
            }}>B</div>
            <span style={{ fontWeight: 900, fontSize: '24px', color: '#1a1a1a', letterSpacing: '-0.5px' }}>BST</span>
          </div>
          <div style={{ fontSize: '14px', color: '#9b9b9b', marginTop: '4px' }}>Подтверждение email</div>
        </div>

        <div style={{ textAlign: 'center', marginBottom: '28px' }}>
          <div style={{ fontSize: '40px', marginBottom: '12px' }}>📨</div>
          <p style={{ margin: '0 0 6px', fontSize: '15px', fontWeight: 700, color: '#1a1a1a' }}>
            Проверьте вашу почту
          </p>
          <p style={{ margin: 0, fontSize: '13px', color: '#6b6b6b', lineHeight: 1.6 }}>
            Мы отправили 6-значный код на<br />
            <strong style={{ color: '#1a1a1a' }}>{maskedEmail}</strong>
          </p>
        </div>

        {/* OTP digits */}
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginBottom: '20px' }} onPaste={handlePaste}>
          {code.map((digit, i) => (
            <input
              key={i}
              ref={el => { inputRefs.current[i] = el }}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={e => handleDigit(i, e.target.value)}
              onKeyDown={e => handleKeyDown(i, e)}
              disabled={loading}
              style={{
                width: '48px',
                height: '56px',
                textAlign: 'center',
                fontSize: '22px',
                fontWeight: 700,
                color: '#1a1a1a',
                background: digit ? '#f0f1ff' : '#f5f5f5',
                border: `2px solid ${error ? '#ef4444' : digit ? '#5b6af0' : '#e0e0e0'}`,
                borderRadius: '12px',
                outline: 'none',
                cursor: loading ? 'not-allowed' : 'text',
                transition: 'border-color 0.15s, background 0.15s',
                caretColor: 'transparent',
              }}
              onFocus={e => { if (!error) e.target.style.borderColor = '#5b6af0' }}
              onBlur={e => { if (!error && !digit) e.target.style.borderColor = '#e0e0e0' }}
            />
          ))}
        </div>

        {error && (
          <div style={{
            padding: '10px 14px',
            background: 'rgba(239,68,68,0.06)',
            border: '1px solid rgba(239,68,68,0.2)',
            borderRadius: '8px',
            fontSize: '13px', color: '#dc2626',
            textAlign: 'center',
            marginBottom: '16px',
          }}>
            {error}
          </div>
        )}

        {loading && (
          <div style={{ textAlign: 'center', marginBottom: '16px' }}>
            <span style={{
              display: 'inline-block', width: '20px', height: '20px',
              border: '2px solid #e0e0e0', borderTopColor: '#5b6af0',
              borderRadius: '50%', animation: 'spin 0.7s linear infinite',
            }} />
          </div>
        )}

        {resendSent && (
          <div style={{
            padding: '10px 14px',
            background: 'rgba(34,197,94,0.06)',
            border: '1px solid rgba(34,197,94,0.2)',
            borderRadius: '8px',
            fontSize: '13px', color: '#16a34a',
            textAlign: 'center',
            marginBottom: '16px',
          }}>
            Новый код отправлен!
          </div>
        )}

        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          <span style={{ fontSize: '13px', color: '#9b9b9b' }}>Не пришёл код? </span>
          {resendCountdown > 0 ? (
            <span style={{ fontSize: '13px', color: '#9b9b9b' }}>
              Повторить через {resendCountdown} с.
            </span>
          ) : (
            <button
              onClick={handleResend}
              disabled={resending}
              style={{
                background: 'none', border: 'none',
                color: '#5b6af0', fontSize: '13px',
                fontWeight: 600, cursor: resending ? 'not-allowed' : 'pointer',
                padding: 0, textDecoration: 'underline', textUnderlineOffset: '2px',
              }}
            >
              {resending ? 'Отправка...' : 'Отправить ещё раз'}
            </button>
          )}
        </div>

        <button
          onClick={() => navigate('/login')}
          style={{
            width: '100%', background: 'none', border: '1px solid #e0e0e0',
            color: '#6b6b6b', fontSize: '13px', cursor: 'pointer',
            padding: '10px', borderRadius: '10px',
            transition: 'border-color 0.15s',
          }}
          onMouseEnter={e => { (e.target as HTMLButtonElement).style.borderColor = '#5b6af0' }}
          onMouseLeave={e => { (e.target as HTMLButtonElement).style.borderColor = '#e0e0e0' }}
        >
          ← Назад ко входу
        </button>
      </div>

      <div style={{ position: 'relative', zIndex: 1, marginTop: '24px', fontSize: '12px', color: '#9b9b9b', textAlign: 'center' }}>
        © {new Date().getFullYear()} BST · Конструктор сайтов нового поколения
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        input::placeholder { color: #9b9b9b; }
      `}</style>
    </div>
  )
}
